import axios, { type AxiosInstance } from "axios";

type Reply = { status: number; headers?: unknown };
type Request = { method?: string; signal?: AbortSignal; headers?: unknown };
const WINDOW_MS = 60_000;

function header(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== "object") {
    return undefined;
  }

  if ("get" in headers && typeof headers.get === "function") {
    const value: unknown = headers.get(name);

    return value == null ? undefined : String(value);
  }

  const value = Object.entries(headers).find(([key]) => key.toLowerCase() === name)?.[1];

  return value == null ? undefined : String(value);
}

function response(error: unknown): Reply | undefined {
  return (error as { response?: Reply } | null)?.response;
}

/** Only Laravel maintenance responses qualify; an ordinary application 503 still fails normally. */
export function maintenanceRetryAfter(error: unknown): string | undefined {
  const reply = response(error);

  return reply?.status === 503 ? header(reply.headers, "retry-after") : undefined;
}

export function isMaintenanceFailure(error: unknown): boolean {
  return maintenanceRetryAfter(error) !== undefined;
}

export function isQuietMaintenanceFailure(error: unknown): boolean {
  return (
    isMaintenanceFailure(error) &&
    !(error as { maintenanceRetryExhausted?: boolean }).maintenanceRetryExhausted &&
    !(response(error) as { maintenanceRetryExhausted?: boolean } | undefined)?.maintenanceRetryExhausted
  );
}

function abortableWait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      reject(new axios.CanceledError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);

    signal.addEventListener("abort", abort);

    if (signal.aborted) {
      abort();
    }
  });
}

/** Reads retry below error/toast/reporting handlers. POST/PATCH require a server-supported idempotency key. */
export async function withMaintenanceRetry<T extends Reply, R = T>(
  send: (signal: AbortSignal, remainingMs: number) => Promise<T>,
  request: Request = {},
  read: (reply: T) => Promise<R> = async (reply) => reply as unknown as R,
): Promise<R> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const started = Date.now();
  const safe =
    ["GET", "HEAD", "OPTIONS", "PUT", "DELETE"].includes((request.method ?? "GET").toUpperCase()) ||
    Boolean(header(request.headers, "idempotency-key"));
  let lastFailure: unknown;
  let attempt = 0;
  let deadline: ReturnType<typeof setTimeout> | undefined;

  request.signal?.addEventListener("abort", abort);

  if (request.signal?.aborted) {
    abort();
  }

  try {
    while (true) {
      if (request.signal?.aborted) {
        throw new axios.CanceledError();
      }

      if (lastFailure && Date.now() - started >= WINDOW_MS) {
        throw lastFailure;
      }

      try {
        const reply = await send(
          controller.signal,
          lastFailure ? Math.max(1, WINDOW_MS - (Date.now() - started)) : Infinity,
        );

        if (!isMaintenanceFailure({ response: reply })) {
          clearTimeout(deadline);
          lastFailure = undefined;

          return await read(reply);
        }

        throw Object.assign(new Error("Service temporarily unavailable"), { response: reply });
      } catch (error) {
        if (!isMaintenanceFailure(error)) {
          throw error;
        }

        lastFailure = error;

        if (!safe) {
          throw error;
        }

        deadline ??= setTimeout(abort, Math.max(0, WINDOW_MS - (Date.now() - started)));
      }

      const retryAfter = header(response(lastFailure)?.headers, "retry-after")?.trim() ?? "";
      const requestedMs = /^\d+$/.test(retryAfter) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now();
      const backoff = Math.min(1000 * 2 ** attempt++, 5000);
      const delay = Math.max(backoff, Number.isFinite(requestedMs) ? Math.min(5000, requestedMs) : 0);

      await abortableWait(Math.min(delay, Math.max(0, WINDOW_MS - (Date.now() - started))), controller.signal);
    }
  } catch (error) {
    if (lastFailure && !request.signal?.aborted && (controller.signal.aborted || Date.now() - started >= WINDOW_MS)) {
      Object.assign(response(lastFailure) ?? {}, { maintenanceRetryExhausted: true });
      throw Object.assign(lastFailure, { maintenanceRetryExhausted: true });
    }

    throw error;
  } finally {
    clearTimeout(deadline);
    request.signal?.removeEventListener("abort", abort);
  }
}

/** Wrap the adapter, so each response/error interceptor runs only once, after the retry window. */
export function installMaintenanceRetry(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const adapter = axios.getAdapter(config.adapter ?? client.defaults.adapter);

    config.adapter = (attempt) =>
      withMaintenanceRetry(
        (signal, remainingMs) =>
          adapter({
            ...attempt,
            signal,
            timeout: Number.isFinite(remainingMs)
              ? Math.min(attempt.timeout || WINDOW_MS, remainingMs)
              : attempt.timeout,
          }),
        { ...config, signal: config.signal as AbortSignal | undefined },
      );

    return config;
  });
}
